"use client";

import { Badge, cn, Loader } from "@devhop/ui";
import { format, isToday } from "date-fns";
import { useEffect } from "react";
import { CalendarDays, Clock, UserRound } from "lucide-react";
import type { ShopShiftAssignmentRow } from "@/modules/merchant/presentation/api/client";
import {
  assignmentStatusBadgeVariant,
  assignmentStatusLabel,
  assignmentStatusRowAccent,
  type ShiftWeekStats,
} from "@/modules/merchant/presentation/lib/shift-assignment-status";
import { formatDateLocal } from "@/shared/lib/date-time";

import {
  formatShiftDuration,
  formatShiftTimeShort,
} from "@/modules/merchant/presentation/lib/shift-time-format";

function ShiftTimeBlock({ row }: { row: ShopShiftAssignmentRow }) {
  if (!row.clockInAt) {
    return (
      <p className="text-muted-foreground text-sm">ຍັງບໍ່ເຂົ້າກະ</p>
    );
  }

  const inTime = formatShiftTimeShort(row.clockInAt);
  const outTime = row.clockOutAt ? formatShiftTimeShort(row.clockOutAt) : null;
  const duration =
    row.clockOutAt != null
      ? formatShiftDuration(row.clockInAt, row.clockOutAt)
      : null;

  return (
    <div className="flex flex-col gap-0.5 sm:items-end">
      <p className="font-medium text-foreground text-sm tabular-nums">
        {inTime}
        <span className="mx-1.5 text-muted-foreground">→</span>
        {outTime ?? "—"}
      </p>
      {duration ? (
        <p className="text-muted-foreground text-xs tabular-nums">
          {duration}
        </p>
      ) : row.clockInAt && !row.clockOutAt ? (
        <p className="font-medium text-emerald-700 text-xs dark:text-emerald-400">
          ກຳລັງເຮັດວຽກ
        </p>
      ) : null}
    </div>
  );
}

function ShiftRow({ row }: { row: ShopShiftAssignmentRow }) {
  const displayName =
    row.staffUserName?.trim() || row.staffUserEmail.split("@")[0];

  return (
    <li
      className={cn(
        "border-l-[3px]",
        assignmentStatusRowAccent(row),
        row.status === "canceled" && "opacity-65",
      )}
    >
      <div className="grid gap-3 px-4 py-3.5 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-4 sm:px-5">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-700 dark:text-violet-300">
            <UserRound className="size-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground text-base">
              {displayName}
            </p>
            {row.staffUserName?.trim() ? (
              <p className="truncate text-muted-foreground text-xs">
                {row.staffUserEmail}
              </p>
            ) : null}
            {row.note?.trim() ? (
              <p className="mt-1 line-clamp-2 text-muted-foreground text-xs">
                {row.note.trim()}
              </p>
            ) : null}
          </div>
        </div>

        <ShiftTimeBlock row={row} />

        <div className="flex items-center sm:justify-end">
          <Badge
            variant={assignmentStatusBadgeVariant(row)}
            className="text-[11px]"
          >
            {assignmentStatusLabel(row)}
          </Badge>
        </div>
      </div>
    </li>
  );
}

function SummaryCards({ stats }: { stats: ShiftWeekStats }) {
  const cards = [
    {
      label: "ທັງໝົດ",
      value: stats.total,
      className: "border-border bg-muted/25",
    },
    {
      label: "ກຳລັງເປີດ",
      value: stats.open,
      className:
        "border-emerald-500/30 bg-emerald-500/10 text-emerald-950 dark:text-emerald-100",
    },
    {
      label: "ປິດແລ້ວ",
      value: stats.closed,
      className: "border-border bg-card",
    },
    {
      label: "ຍັງບໍ່ເຂົ້າ",
      value: stats.scheduled,
      className:
        "border-amber-500/30 bg-amber-500/10 text-amber-950 dark:text-amber-100",
    },
  ] as const;

  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
      {cards.map((card) => (
        <div
          key={card.label}
          className={cn(
            "rounded-xl border px-3 py-3 sm:px-4 sm:py-3.5",
            card.className,
          )}
        >
          <p className="font-medium text-[10px] text-muted-foreground uppercase tracking-wide">
            {card.label}
          </p>
          <p className="mt-1 font-bold font-heading text-2xl tabular-nums tracking-tight">
            {card.value}
          </p>
        </div>
      ))}
    </div>
  );
}

export type ShopShiftListProps = {
  weekDays: Date[];
  byDate: Map<string, ShopShiftAssignmentRow[]>;
  stats: ShiftWeekStats;
  loading: boolean;
  showEmptyDays: boolean;
  scrollToDayKey: string | null;
  onScrolledToDay: () => void;
};

export function ShopShiftList({
  weekDays,
  byDate,
  stats,
  loading,
  showEmptyDays,
  scrollToDayKey,
  onScrolledToDay,
}: ShopShiftListProps) {
  const visibleDays = weekDays.filter((day) => {
    const key = format(day, "yyyy-MM-dd");
    const rows = byDate.get(key) ?? [];
    return showEmptyDays || rows.length > 0;
  });

  useEffect(() => {
    if (!scrollToDayKey) return;
    const el = document.getElementById(`shift-day-${scrollToDayKey}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    onScrolledToDay();
  }, [scrollToDayKey, onScrolledToDay]);

  if (loading) {
    return (
      <div className="flex justify-center rounded-xl border border-border bg-card py-20 shadow-sm">
        <Loader />
      </div>
    );
  }

  const hasAny = stats.total > 0 || stats.canceled > 0;

  return (
    <>
      <SummaryCards stats={stats} />

      {!hasAny ? (
        <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-violet-500/10 text-violet-700 dark:text-violet-300">
              <CalendarDays className="size-7" aria-hidden />
            </div>
            <p className="font-heading font-semibold text-foreground text-lg">
              ບໍ່ມີກະໃນອາທິດນີ້
            </p>
            <p className="mt-1 max-w-sm text-muted-foreground text-sm">
              ພະນັກງານເປີດກະຈາກໜ້າ POS — ລາຍການຈະຂຶ້ນຫຼັງເປີດກະ
            </p>
          </div>
        </section>
      ) : visibleDays.length === 0 ? (
        <section className="rounded-xl border border-border bg-card px-6 py-12 text-center shadow-sm">
          <p className="text-muted-foreground text-sm">
            ທຸກວັນໃນອາທິດນີ້ບໍ່ມີກະ — ເປີດ «ສະແດງວັນທີ່ບໍ່ມີກະ» ເພື່ອເບິ່ງປະຕິທິນເຕັມ
          </p>
        </section>
      ) : (
        <div className="space-y-4">
          {visibleDays.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const rows = byDate.get(key) ?? [];
            const today = isToday(day);

            return (
              <section
                key={key}
                id={`shift-day-${key}`}
                className={cn(
                  "scroll-mt-4 overflow-hidden rounded-xl border bg-card shadow-sm",
                  today
                    ? "border-emerald-500/40 ring-1 ring-emerald-500/20"
                    : "border-border",
                )}
              >
                <div
                  className={cn(
                    "flex items-center justify-between border-b px-4 py-3 sm:px-5",
                    today
                      ? "border-emerald-500/25 bg-emerald-500/10"
                      : "border-border bg-muted/25",
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-heading font-semibold text-foreground text-base">
                      {formatDateLocal(key)}
                    </h2>
                    {today ? (
                      <Badge variant="success" className="text-[10px]">
                        ມື້ນີ້
                      </Badge>
                    ) : null}
                  </div>
                  <span className="inline-flex items-center gap-1 font-medium text-muted-foreground text-xs tabular-nums">
                    <Clock className="size-3.5" aria-hidden />
                    {rows.length} ກະ
                  </span>
                </div>

                {rows.length === 0 ? (
                  <p className="px-4 py-8 text-center text-muted-foreground text-sm sm:px-5">
                    ບໍ່ມີກະ
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {rows.map((row) => (
                      <ShiftRow key={row.id} row={row} />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
